<template>
  <div class="relative">
    <!-- Notification Bell Button -->
    <button
      @click="toggleNotifications"
      class="relative rounded-full p-2 transition-colors duration-160 focus:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40 focus-visible:ring-offset-2"
      :class="isOpen ? 'text-mc-blue' : 'text-on-dark'"
      aria-label="Notifications"
    >
      <!-- Bell Icon -->
      <svg
        class="h-6 w-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>

      <!-- Badge (Orange Dot) -->
      <span
        v-if="notificationCount > 0"
        class="absolute top-1 right-1 block h-2.5 w-2.5 rounded-full bg-mc-red ring-2 ring-mc-white"
      ></span>

      <!-- Badge with Number (for multiple notifications) -->
      <span
        v-if="notificationCount > 1"
        class="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 text-xs font-bold text-mc-white bg-mc-red rounded-full ring-2 ring-mc-white"
      >
        {{ notificationCount > 9 ? '9+' : notificationCount }}
      </span>
    </button>

    <!-- Notifications Dropdown -->
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="absolute right-0 mt-2 w-80 origin-top-right rounded-mc-sm bg-mc-white dark:bg-mc-charcoal shadow-mc-md border border-mc-steel dark:border-zinc-700 focus:outline-none z-50"
      >
        <!-- Header -->
        <div class="px-4 py-3 border-b border-mc-steel dark:border-zinc-700">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-semibold font-body text-mc-ink dark:text-on-dark">Notifications</h3>
            <button
              v-if="notifications.length > 0"
              @click="clearAllNotifications"
              class="text-xs font-body font-medium text-mc-blue hover:text-mc-blue/80 transition-colors duration-160"
            >
              Clear all
            </button>
          </div>
        </div>

        <!-- Notifications List -->
        <div class="max-h-96 overflow-y-auto">
          <!-- No notifications -->
          <div
            v-if="notifications.length === 0"
            class="px-4 py-8 text-center text-sm text-ink-muted dark:text-on-dark-muted"
          >
            <svg
              class="mx-auto h-12 w-12 text-ink-muted dark:text-on-dark-muted mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p class="font-body">No notifications</p>
          </div>

          <!-- Notification Items -->
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="px-4 py-3 hover:bg-mc-cloud dark:hover:bg-mc-ink/10 cursor-pointer border-b border-mc-steel/50 dark:border-zinc-700 last:border-b-0 transition-colors duration-160"
            @click="handleNotificationClick(notification)"
          >
            <div class="flex items-start space-x-3">
              <!-- Icon -->
              <div
                class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                :class="notification.iconBgClass || 'bg-mc-blue-100'"
              >
                <component
                  :is="notification.icon"
                  v-if="notification.icon"
                  class="w-6 h-6"
                  :class="notification.iconClass || 'text-mc-blue'"
                />
                <svg
                  v-else
                  class="w-6 h-6"
                  :class="notification.iconClass || 'text-mc-blue'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-body font-medium text-mc-ink dark:text-on-dark">
                  {{ notification.title }}
                </p>
                <p class="text-sm font-body text-ink-muted dark:text-on-dark-muted mt-1">
                  {{ notification.message }}
                </p>
                <p
                  v-if="notification.time"
                  class="text-xs font-mono text-ink-muted dark:text-on-dark-muted mt-1"
                >
                  {{ notification.time }}
                </p>
              </div>

              <!-- Dismiss button -->
              <button
                @click.stop="dismissNotification(notification.id)"
                class="flex-shrink-0 text-ink-muted dark:text-on-dark-muted hover:text-mc-ink dark:hover:text-on-dark transition-colors duration-160"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Click outside to close -->
    <div
      v-if="isOpen"
      @click="isOpen = false"
      class="fixed inset-0 z-40"
    ></div>
  </div>
</template>

<script>
export default {
  name: 'NotificationBell',
  data() {
    return {
      isOpen: false,
      notifications: [],
      deferredPrompt: null // Store the install prompt
    }
  },
  computed: {
    notificationCount() {
      return this.notifications.length;
    }
  },
  mounted() {
    // Listen for new notifications from other components
    window.addEventListener('add-notification', this.addNotification);
    
    // Check if beforeinstallprompt already fired (stored in window.deferredPrompt by main.js)
    if (window.deferredPrompt) {
      this.deferredPrompt = window.deferredPrompt;
    }
    
    // Listen for future beforeinstallprompt events (in case component mounts before event)
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
    
    // Listen for app installation to remove notification
    window.addEventListener('appinstalled', this.handleAppInstalled);
    
    // Check for PWA install notification
    this.checkPWAInstallNotification();
  },
  beforeUnmount() {
    window.removeEventListener('add-notification', this.addNotification);
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
    window.removeEventListener('appinstalled', this.handleAppInstalled);
  },
  methods: {
    toggleNotifications() {
      this.isOpen = !this.isOpen;
    },

    checkPWAInstallNotification() {
      // First, check if beforeinstallprompt already fired (stored in window.deferredPrompt)
      if (window.deferredPrompt) {
        localStorage.removeItem('pwa-ever-installed');
        localStorage.removeItem('pwa-last-standalone-check');
      }
      
      // Check if app is already installed using proper detection
      const isInstalled = this.checkIfPWAInstalled();
      const isDismissed = localStorage.getItem('pwa-install-notification-dismissed') === 'true';

      if (!isInstalled && !isDismissed) {
        this.notifications.push({
          id: 'pwa-install',
          type: 'pwa-install',
          title: 'Install MeshPortal App',
          message: 'Get quick access from your home screen',
          iconBgClass: 'bg-mc-blue-100',
          iconClass: 'text-mc-blue',
          time: 'Now',
          action: 'install-pwa'
        });
      }
    },

    checkIfPWAInstalled() {
      // First check if running in standalone mode (currently in installed app)
      const isStandalone = 
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
        (window.navigator.standalone === true) ||
        (window.matchMedia && window.matchMedia('(display-mode: minimal-ui)').matches) ||
        (window.matchMedia && window.matchMedia('(display-mode: fullscreen)').matches);

      if (isStandalone) {
        // Mark that app is installed using localStorage so browser knows
        localStorage.setItem('pwa-ever-installed', 'true');
        localStorage.setItem('pwa-last-standalone-check', Date.now().toString());
        return true;
      }

      // If in browser, check if app was ever installed on this device
      const wasEverInstalled = localStorage.getItem('pwa-ever-installed') === 'true';
      const lastCheck = localStorage.getItem('pwa-last-standalone-check');
      
      // Only trust recent standalone checks (within last 24 hours)
      if (wasEverInstalled && lastCheck) {
        const timeSinceLastCheck = Date.now() - parseInt(lastCheck);
        const hoursAgo = timeSinceLastCheck / (1000 * 60 * 60);
        
        // Only consider installed if used in standalone mode within last 24 hours
        if (hoursAgo < 24) {
          return true;
        } else {
          // Too old, clear flags
          localStorage.removeItem('pwa-ever-installed');
          localStorage.removeItem('pwa-last-standalone-check');
          return false;
        }
      }

      return false;
    },

    addNotification(event) {
      const notification = {
        id: Date.now().toString(),
        ...event.detail
      };
      this.notifications.unshift(notification);
    },

    handleNotificationClick(notification) {
      if (notification.action === 'install-pwa') {
        // Trigger PWA install directly
        this.triggerPWAInstall();
        // Dismiss this notification
        this.dismissNotification(notification.id);
      } else if (notification.onClick) {
        notification.onClick();
      }
    },

    async triggerPWAInstall() {
      if (!this.deferredPrompt) {
        this.showManualInstallInstructions();
        return;
      }

      try {
        this.deferredPrompt.prompt();
        await this.deferredPrompt.userChoice;
        this.deferredPrompt = null;
        window.deferredPrompt = null;
      } catch (error) {
        console.error('PWA install failed:', error);
        this.showManualInstallInstructions();
      }
    },

    showManualInstallInstructions() {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      const isEdge = /Edge|Edg/.test(navigator.userAgent);
      
      let message = '';
      
      if (isIOS) {
        message = 'To install this app on iOS:\n\n1. Tap the Share button (square with arrow)\n2. Scroll down and select "Add to Home Screen"\n3. Tap "Add"';
      } else if (isAndroid) {
        message = 'To install this app:\n\n1. Tap the menu button (⋮) in your browser\n2. Select "Add to Home Screen" or "Install App"\n3. Tap "Install"';
      } else if (isEdge) {
        message = 'To install this app in Edge:\n\n1. Click the menu (⋯) → Apps → "Install this site as an app"\n2. Or look for the install icon in the address bar';
      } else {
        message = 'To install this app:\n\n1. Look for the install icon in your browser\'s address bar\n2. Or check your browser\'s menu for "Install" option';
      }
      
      alert(message);
    },

    handleBeforeInstallPrompt(e) {
      e.preventDefault();
      this.deferredPrompt = e;
      
      // Also store it globally for other components
      window.deferredPrompt = e;
      
      // Clear stale installed flags
      localStorage.removeItem('pwa-ever-installed');
      localStorage.removeItem('pwa-last-standalone-check');
      
      // Add install notification if not already present and not dismissed
      const hasInstallNotification = this.notifications.some(n => n.type === 'pwa-install');
      const isDismissed = localStorage.getItem('pwa-install-notification-dismissed') === 'true';
      
      if (!hasInstallNotification && !isDismissed) {
        this.notifications.unshift({
          id: 'pwa-install',
          type: 'pwa-install',
          title: 'Install MeshPortal App',
          message: 'Get quick access from your home screen',
          iconBgClass: 'bg-mc-blue-100',
          iconClass: 'text-mc-blue',
          time: 'Now',
          action: 'install-pwa'
        });
      }
    },

    handleAppInstalled() {
      // Remove the PWA install notification
      this.notifications = this.notifications.filter(n => n.type !== 'pwa-install');
      
      // Mark as installed
      localStorage.setItem('pwa-ever-installed', 'true');
      localStorage.setItem('pwa-last-standalone-check', Date.now().toString());
    },

    dismissNotification(id) {
      const notification = this.notifications.find(n => n.id === id);
      
      if (notification && notification.type === 'pwa-install') {
        // Remember that user dismissed PWA install notification
        localStorage.setItem('pwa-install-notification-dismissed', 'true');
      }
      
      this.notifications = this.notifications.filter(n => n.id !== id);
    },

    clearAllNotifications() {
      // Mark PWA notification as dismissed if present
      const hasPWANotification = this.notifications.some(n => n.type === 'pwa-install');
      if (hasPWANotification) {
        localStorage.setItem('pwa-install-notification-dismissed', 'true');
      }
      
      this.notifications = [];
    },

    // Public method to add notification from parent
    addNotificationFromParent(notification) {
      this.notifications.unshift({
        id: Date.now().toString(),
        time: 'Now',
        ...notification
      });
    }
  }
}
</script>

<style scoped>
/* Ensure dropdown appears above other content */
.z-50 {
  z-index: 50;
}

.z-40 {
  z-index: 40;
}
</style>
